/**
 * The 108 — Booking Widget
 * ------------------------
 * Standalone Shopify theme asset. No build step, no dependencies.
 * Upload as-is via Shopify Admin → Online Store → Themes → Edit code → Assets → Add a new asset.
 *
 * Usage: add a container with the right data attributes anywhere on a
 * product page (see the matching Liquid snippet for each of the 4 products),
 * then load this file with `defer`.
 *
 *   <div class="the108-booking-widget"
 *        data-session-type="cage_coach"
 *        data-durations="60"
 *        data-needs-coach="true"></div>
 *
 * What it does:
 *   1. Lets the shopper pick duration (if the product offers a choice),
 *      coach level (if the product requires one), a date, and a time slot.
 *   2. Calls the108-booking-widget Edge Function for live availability.
 *   3. On slot selection, places a 10-minute hold and adds the matching
 *      Shopify variant to the real cart via /cart/add.js, carrying the
 *      booking reference as hidden line-item properties.
 *   4. Redirects to /cart so checkout (and the 10-minute clock) starts
 *      immediately.
 *
 * Known limitation: the date picker's day boundaries are computed from the
 * shopper's own browser clock, not corrected for the venue's London timezone.
 * Slot times themselves are always correct (fetched from the engine, which
 * is timezone-aware) — the only edge case is a shopper browsing very close
 * to midnight might need to tap "next day" to see a slot that's technically
 * still "today" in London. Not fixed here to avoid a client-side timezone
 * library for a cosmetic edge case.
 */
(function () {
  "use strict";

  const FUNCTIONS_URL = "https://lmjgbhzjwxxcybhrlwzw.supabase.co/functions/v1/the108-booking-widget";
  const ANON_KEY = "sb_publishable_0X6E1hBfYmQq_GLModphpg_qUttqowb";
  const LOOKAHEAD_DAYS = 14;
  const LONDON_TZ = "Europe/London";

  const COACH_LABELS = { standard: "Standard Coach", senior: "Senior Coach" };
  let CACHED_WAIVER; // undefined = not yet fetched, null = no waiver configured, object = {title, body}

  function css() {
    return `
      .the108-bw { --accent: #009dd1; font: inherit; color: inherit; max-width: 640px; margin: 24px 0; }
      .the108-bw * { box-sizing: border-box; }
      .the108-bw-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
      .the108-bw-choice { padding: 8px 16px; border: 1px solid #d5d5d5; border-radius: 999px; background: #fff; cursor: pointer; font: inherit; font-size: 14px; transition: all .15s ease; }
      .the108-bw-choice:hover { border-color: var(--accent); }
      .the108-bw-choice.is-active { background: var(--accent); border-color: var(--accent); color: #fff; }
      .the108-bw-datepicker { display: block; width: 100%; max-width: 260px; padding: 10px 14px; border: 1px solid #d5d5d5; border-radius: 10px; background: #fff; font: inherit; font-size: 15px; margin-bottom: 16px; color: inherit; }
      .the108-bw-datepicker:focus { outline: none; border-color: var(--accent); }
      .the108-bw-date-row { display: flex; align-items: center; gap: 8px; max-width: 260px; margin-bottom: 16px; }
      .the108-bw-date-row .the108-bw-datepicker { margin-bottom: 0; flex: 1 1 auto; }
      .the108-bw-date-nav { flex: 0 0 auto; width: 36px; height: 36px; border: 1px solid #d5d5d5; border-radius: 10px; background: #fff; font-size: 18px; line-height: 1; color: inherit; cursor: pointer; display: flex; align-items: center; justify-content: center; }
      .the108-bw-date-nav:hover:not(:disabled) { border-color: var(--accent); }
      .the108-bw-date-nav:disabled { opacity: .35; cursor: default; }
      .the108-bw-input-field { display: block; width: 100%; padding: 10px 14px; border: 1px solid #d5d5d5; border-radius: 10px; background: #fff; font: inherit; font-size: 15px; color: inherit; }
      .the108-bw-input-field:focus { outline: none; border-color: var(--accent); }
      .the108-bw-slots { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 8px; }
      .the108-bw-slot { padding: 10px 6px; border: 1px solid #d5d5d5; border-radius: 8px; background: #fff; cursor: pointer; font: inherit; font-size: 14px; text-align: center; line-height: 1.3; transition: all .15s ease; }
      .the108-bw-slot:hover:not(:disabled) { border-color: var(--accent); }
      .the108-bw-slot .price { display: block; font-size: 12px; opacity: .65; margin-top: 2px; }
      .the108-bw-slot:disabled { opacity: .4; cursor: default; }
      .the108-bw-slot.is-loading { opacity: .5; pointer-events: none; }
      .the108-bw-empty, .the108-bw-error, .the108-bw-loading { padding: 20px 4px; font-size: 14px; opacity: .7; }
      .the108-bw-error { color: #c0392b; opacity: 1; }
      .the108-bw-waiver-text { max-height: 160px; overflow-y: auto; border: 1px solid #d5d5d5; border-radius: 10px;
        padding: 12px 14px; font-size: 12.5px; line-height: 1.6; color: #444; background: #fafafa; margin-bottom: 10px; }
      .the108-bw-waiver-agree { display: flex; align-items: flex-start; gap: 8px; font-size: 13px; line-height: 1.4;
        cursor: pointer; margin-bottom: 16px; }
      .the108-bw-waiver-agree input { margin-top: 2px; flex: 0 0 auto; }
      .the108-bw-qty { display: flex; align-items: center; gap: 12px; margin-top: 12px; }
      .the108-bw-qty button { width: 36px; height: 36px; border-radius: 8px; border: 1px solid #d5d5d5; background: #fff; font-size: 18px; cursor: pointer; }
      .the108-bw-qty button:disabled { opacity: .35; cursor: default; }
      .the108-bw-qty .count { min-width: 24px; text-align: center; font-weight: 600; font-size: 16px; }
      .the108-bw-qty .cap-note { font-size: 12px; opacity: .6; margin-left: 4px; }
      .the108-bw-confirm { display: block; width: 100%; margin-top: 12px; padding: 12px; border: none; border-radius: 8px; background: var(--accent); color: #fff; font: inherit; font-size: 15px; font-weight: 600; cursor: pointer; }
      .the108-bw-confirm:disabled { opacity: .5; cursor: default; }
      .the108-bw-slot.is-selected { background: var(--accent); border-color: var(--accent); color: #fff; }
      .the108-bw-back { background: none; border: none; padding: 0; margin-bottom: 16px; font: inherit; font-size: 14px; color: inherit; opacity: .7; cursor: pointer; }
      .the108-bw-back:hover { opacity: 1; }
      .the108-bw-review-title { font-size: 20px; font-weight: 700; margin-bottom: 4px; }
      .the108-bw-review-sub { font-size: 14px; opacity: .65; margin-bottom: 16px; }
      .the108-bw-review-card { border: 1px solid #e2e2e2; border-radius: 12px; overflow: hidden; margin-bottom: 16px; }
      .the108-bw-review-row { display: flex; justify-content: space-between; align-items: center; padding: 14px 16px; border-bottom: 1px solid #ececec; font-size: 15px; }
      .the108-bw-review-row:last-child { border-bottom: none; }
      .the108-bw-review-row span:first-child { opacity: .65; }
      .the108-bw-label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; opacity: .6; margin-bottom: 8px; }
    `;
  }

  function injectStyle() {
    if (document.getElementById("the108-bw-style")) return;
    const s = document.createElement("style");
    s.id = "the108-bw-style";
    s.textContent = css();
    document.head.appendChild(s);
  }

  async function callWidget(action, payload) {
    const res = await fetch(FUNCTIONS_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: ANON_KEY },
      body: JSON.stringify({ action, ...payload }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status !== "ok") throw new Error(data.message || `Request failed (${res.status})`);
    return data;
  }

  function fmtDay(date) {
    return {
      dow: date.toLocaleDateString("en-GB", { weekday: "short", timeZone: LONDON_TZ }),
      dom: date.toLocaleDateString("en-GB", { day: "numeric", timeZone: LONDON_TZ }),
    };
  }

  function fmtTime(iso) {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", timeZone: LONDON_TZ });
  }

  function fmtPrice(pence) {
    return `£${(pence / 100).toFixed(2)}`;
  }

  // Converts a London wall-clock time (given as separate y/m/d + minutes
  // since midnight) into the correct UTC Date, regardless of the browser's
  // own system timezone. Needed because naive `new Date(y,m,d).setMinutes(n)`
  // interprets the time in the BROWSER's local timezone, not London's — if
  // the browser isn't set to UK time, opening-hours boundaries shift by
  // whatever the offset is, badly distorting the availability window.
  function londonOffsetMinutes(utcDate) {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: LONDON_TZ, hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
    const parts = dtf.formatToParts(utcDate).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
    const asIfUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return (asIfUTC - utcDate.getTime()) / 60000; // e.g. +60 during BST, 0 during GMT
  }

  function londonWallTimeToUTC(y, m, d, minutesFromMidnight) {
    const guess = new Date(Date.UTC(y, m, d, 0, minutesFromMidnight));
    const offset = londonOffsetMinutes(guess);
    return new Date(guess.getTime() - offset * 60000);
  }

  function gidToNumeric(gid) {
    const parts = String(gid).split("/");
    return parts[parts.length - 1];
  }

  class BookingWidget {
    constructor(el) {
      this.el = el;
      this.sessionType = el.dataset.sessionType;
      this.durations = (el.dataset.durations || "60").split(",").map((d) => parseInt(d.trim(), 10));
      this.needsCoach = el.dataset.needsCoach === "true";

      this.duration = this.durations[0];
      this.coachLevel = this.needsCoach ? "standard" : null;
      this.coachId = null;
      this.coachName = null;
      this.selectedDate = new Date();
      this.slots = [];

      this.render();
      this.applyBrandColor();
      this.loadAvailability();
    }

    async applyBrandColor() {
      try {
        const data = await callWidget("brand", {});
        if (data.brand_color) this.el.style.setProperty("--accent", data.brand_color);
      } catch (e) {
        // Non-critical — widget still works fine with the default accent.
      }
    }

    render() {
      this.el.classList.add("the108-bw");
      this.el.innerHTML = `
        <div data-role="picker">
          ${this.durations.length > 1 ? `
            <div class="the108-bw-label">Duration</div>
            <div class="the108-bw-row" data-role="durations">
              ${this.durations.map((d) => `<button type="button" class="the108-bw-choice" data-duration="${d}">${d} min</button>`).join("")}
            </div>` : ""}
          ${this.needsCoach ? `
            <div class="the108-bw-label">Coach</div>
            <div class="the108-bw-row" data-role="coach-levels">
              <button type="button" class="the108-bw-choice" data-level="standard">Standard</button>
              <button type="button" class="the108-bw-choice" data-level="senior">Senior</button>
            </div>
            <div class="the108-bw-row" data-role="coach-names" style="margin-top:-4px"></div>` : ""}
          <div class="the108-bw-label">Date</div>
          <div class="the108-bw-date-row">
            <button type="button" class="the108-bw-date-nav" data-role="date-prev" aria-label="Previous day">‹</button>
            <input type="date" class="the108-bw-datepicker" data-role="datepicker" />
            <button type="button" class="the108-bw-date-nav" data-role="date-next" aria-label="Next day">›</button>
          </div>
          <div class="the108-bw-label">Time</div>
          <div data-role="slots"><div class="the108-bw-loading">Loading times…</div></div>
        </div>
        <div data-role="review" style="display:none"></div>
      `;

      this.wireDatePicker();
      this.updateChoiceButtons();

      this.el.querySelectorAll("[data-duration]").forEach((btn) =>
        btn.addEventListener("click", () => {
          this.duration = parseInt(btn.dataset.duration, 10);
          this.updateChoiceButtons();
          this.loadAvailability();
        })
      );
      this.el.querySelectorAll("[data-level]").forEach((btn) =>
        btn.addEventListener("click", () => {
          this.coachLevel = btn.dataset.level;
          this.coachId = null;
          this.coachName = null;
          this.updateChoiceButtons();
          this.loadCoachNames();
          this.loadAvailability();
        })
      );
      if (this.needsCoach) this.loadCoachNames();
    }

    async loadCoachNames() {
      const wrap = this.el.querySelector('[data-role="coach-names"]');
      if (!wrap || !this.coachLevel) return;
      wrap.innerHTML = "";
      try {
        const data = await callWidget("coaches", {
          session_type: this.sessionType, duration_mins: this.duration, coach_level: this.coachLevel,
        });
        const coaches = data.coaches || [];
        if (!coaches.length) return;
        const noPref = document.createElement("button");
        noPref.type = "button";
        noPref.className = "the108-bw-choice is-active";
        noPref.textContent = "No preference";
        noPref.addEventListener("click", () => this.selectCoachId(null, noPref, null));
        wrap.appendChild(noPref);
        coaches.forEach((c) => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "the108-bw-choice";
          btn.textContent = c.name;
          btn.addEventListener("click", () => this.selectCoachId(c.resource_id, btn, c.name));
          wrap.appendChild(btn);
        });
      } catch (e) { /* silently fall back to "any coach of this level" */ }
    }

    selectCoachId(id, btn, name) {
      this.coachId = id;
      this.coachName = name || null;
      this.el.querySelectorAll('[data-role="coach-names"] .the108-bw-choice').forEach((b) => b.classList.remove("is-active"));
      btn.classList.add("is-active");
      this.loadAvailability();
    }

    sessionLabel() {
      const base = this.sessionType.startsWith("cage") ? "Cage" : "Bullpen";
      const coachSuffix = this.needsCoach ? " + Coach" : "";
      return this.durations.length > 1 ? `${this.duration} Minute ${base}${coachSuffix}` : `${base}${coachSuffix} (${this.duration} min)`;
    }

    async loadWaiver() {
      if (typeof CACHED_WAIVER !== "undefined") return CACHED_WAIVER;
      try {
        const data = await callWidget("waiver", {});
        CACHED_WAIVER = data.waiver || null;
      } catch (e) { CACHED_WAIVER = null; }
      return CACHED_WAIVER;
    }

    async showReview(slot, quantity) {
      this.el.querySelector('[data-role="picker"]').style.display = "none";
      const review = this.el.querySelector('[data-role="review"]');
      review.style.display = "block";

      const unitPrice = slot.price_cents;
      const total = unitPrice * quantity;
      const dateLabel = new Date(slot.slot_start).toLocaleDateString("en-GB", { timeZone: LONDON_TZ, weekday: "long", day: "numeric", month: "long" });
      const timeLabel = `${fmtTime(slot.slot_start)} – ${fmtTime(slot.slot_end)}`;
      const waiver = await this.loadWaiver();
      this.waiverCovered = false;

      review.innerHTML = `
        <button type="button" class="the108-bw-back" data-role="back">← Pick another time</button>
        <div class="the108-bw-review-title">Confirm your booking</div>
        <div class="the108-bw-review-sub">Review the details below.</div>
        <div class="the108-bw-review-card">
          <div class="the108-bw-review-row"><span>Session</span><strong>${this.sessionLabel()}</strong></div>
          <div class="the108-bw-review-row"><span>Date</span><strong>${dateLabel}</strong></div>
          <div class="the108-bw-review-row"><span>Time</span><strong>${timeLabel}</strong></div>
          ${!this.needsCoach && slot.free_count > 1 ? `
          <div class="the108-bw-review-row"><span>How many ${this.sessionType}s?</span>
            <span class="the108-bw-qty" style="margin-top:0">
              <button type="button" data-role="rev-minus">−</button>
              <span class="count" data-role="rev-count">${quantity}</span>
              <button type="button" data-role="rev-plus">+</button>
            </span>
          </div>
          <div class="the108-bw-review-row"><span></span><span class="cap-note" data-role="rev-cap">${slot.free_count} free at this time</span></div>` : ""}
          <div class="the108-bw-review-row"><span>Price</span><strong data-role="rev-price">${fmtPrice(total)}</strong></div>
        </div>

        <div class="the108-bw-label">Your details</div>
        <div class="the108-bw-row" style="flex-direction:column;align-items:stretch;gap:8px">
          <input type="text" class="the108-bw-input-field" data-role="rev-name" placeholder="Full name" autocomplete="name">
          <input type="email" class="the108-bw-input-field" data-role="rev-email" placeholder="Email" autocomplete="email">
        </div>

        ${waiver ? `<div data-role="waiver-box">
          <div class="the108-bw-label" style="margin-top:16px">${waiver.title || "Waiver"}</div>
          <div class="the108-bw-waiver-text">${String(waiver.body || "").replace(/</g, "&lt;").replace(/\n/g, "<br>")}</div>
          <label class="the108-bw-waiver-agree"><input type="checkbox" data-role="rev-agree"> I have read and agree to the waiver above, and I'm signing on my own behalf.</label>
        </div>` : ""}

        <button type="button" class="the108-bw-confirm" data-role="rev-confirm">Hold this slot →</button>
        <div data-role="rev-err"></div>
      `;

      let qty = quantity;
      const updatePrice = () => {
        review.querySelector('[data-role="rev-count"]').textContent = qty;
        review.querySelector('[data-role="rev-price"]').textContent = fmtPrice(unitPrice * qty);
        const minusBtn = review.querySelector('[data-role="rev-minus"]');
        const plusBtn = review.querySelector('[data-role="rev-plus"]');
        if (minusBtn) minusBtn.disabled = qty <= 1;
        if (plusBtn) plusBtn.disabled = qty >= slot.free_count;
      };
      const minusBtn = review.querySelector('[data-role="rev-minus"]');
      const plusBtn = review.querySelector('[data-role="rev-plus"]');
      if (minusBtn) minusBtn.addEventListener("click", () => { if (qty > 1) { qty--; updatePrice(); } });
      if (plusBtn) plusBtn.addEventListener("click", () => { if (qty < slot.free_count) { qty++; updatePrice(); } });

      // Skip the waiver for someone who's already signed — same pattern as
      // the native widget: only counts if BOTH name and email match a prior
      // signature, so a shared family email can't inherit someone else's.
      if (waiver) {
        const nameEl = review.querySelector('[data-role="rev-name"]');
        const emailEl = review.querySelector('[data-role="rev-email"]');
        const recheck = async () => {
          const nm = (nameEl.value || "").trim();
          const em = (emailEl.value || "").trim();
          const box = review.querySelector('[data-role="waiver-box"]');
          if (!nm || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { this.waiverCovered = false; return; }
          try {
            const status = await callWidget("waiver_status", { name: nm, email: em });
            this.waiverCovered = !!status.covered;
            if (box) box.style.display = this.waiverCovered ? "none" : "block";
          } catch (e) { /* fall back to requiring the checkbox */ }
        };
        nameEl.addEventListener("blur", recheck);
        emailEl.addEventListener("blur", recheck);
      }

      review.querySelector('[data-role="back"]').addEventListener("click", () => this.backToPicker());
      review.querySelector('[data-role="rev-confirm"]').addEventListener("click", (e) => {
        this.validateAndConfirm(slot, e.target, qty, waiver);
      });
    }

    validateAndConfirm(slot, btn, quantity, waiver) {
      const review = this.el.querySelector('[data-role="review"]');
      const err = review.querySelector('[data-role="rev-err"]');
      err.textContent = "";
      const name = (review.querySelector('[data-role="rev-name"]').value || "").trim();
      const email = (review.querySelector('[data-role="rev-email"]').value || "").trim();
      if (!name) { err.textContent = "Please enter your name."; return; }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { err.textContent = "Please enter a valid email."; return; }
      const agreeEl = review.querySelector('[data-role="rev-agree"]');
      if (waiver && !this.waiverCovered && agreeEl && !agreeEl.checked) {
        err.textContent = "Please read and agree to the waiver to continue.";
        return;
      }
      this.confirmAndHold(slot, btn, quantity, { name, email, waiver, alreadyCovered: this.waiverCovered });
    }

    backToPicker() {
      this.el.querySelector('[data-role="review"]').style.display = "none";
      this.el.querySelector('[data-role="picker"]').style.display = "block";
    }

    updateChoiceButtons() {
      this.el.querySelectorAll("[data-duration]").forEach((btn) =>
        btn.classList.toggle("is-active", parseInt(btn.dataset.duration, 10) === this.duration)
      );
      this.el.querySelectorAll("[data-level]").forEach((btn) =>
        btn.classList.toggle("is-active", btn.dataset.level === this.coachLevel)
      );
    }

    wireDatePicker() {
      const input = this.el.querySelector('[data-role="datepicker"]');
      const prevBtn = this.el.querySelector('[data-role="date-prev"]');
      const nextBtn = this.el.querySelector('[data-role="date-next"]');
      const toISODate = (d) => {
        const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
      };
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date();
      maxDate.setDate(maxDate.getDate() + LOOKAHEAD_DAYS - 1);
      maxDate.setHours(0, 0, 0, 0);
      input.min = toISODate(today);
      input.max = toISODate(maxDate);

      const syncNavButtons = () => {
        const d = new Date(this.selectedDate);
        d.setHours(0, 0, 0, 0);
        prevBtn.disabled = d <= today;
        nextBtn.disabled = d >= maxDate;
      };

      const applyDate = () => {
        input.value = toISODate(this.selectedDate);
        syncNavButtons();
        this.loadAvailability();
      };

      input.value = toISODate(this.selectedDate);
      syncNavButtons();

      input.addEventListener("change", () => {
        if (!input.value) return;
        const [y, m, d] = input.value.split("-").map(Number);
        this.selectedDate = new Date(y, m - 1, d);
        syncNavButtons();
        this.loadAvailability();
      });

      prevBtn.addEventListener("click", () => {
        if (prevBtn.disabled) return;
        const d = new Date(this.selectedDate);
        d.setDate(d.getDate() - 1);
        if (d < today) return; // guard against skipping past the allowed range
        this.selectedDate = d;
        applyDate();
      });

      nextBtn.addEventListener("click", () => {
        if (nextBtn.disabled) return;
        const d = new Date(this.selectedDate);
        d.setDate(d.getDate() + 1);
        if (d > maxDate) return;
        this.selectedDate = d;
        applyDate();
      });
    }

    async loadAvailability() {
      const slotsWrap = this.el.querySelector('[data-role="slots"]');
      slotsWrap.innerHTML = '<div class="the108-bw-loading">Loading times…</div>';
      this.selectedSlot = null;

      const y = this.selectedDate.getFullYear();
      const m = this.selectedDate.getMonth();
      const d = this.selectedDate.getDate();
      const dateStr = `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

      try {
        const hours = await callWidget("hours", { date: dateStr });
        const from = londonWallTimeToUTC(y, m, d, hours.open_minute);
        const to = londonWallTimeToUTC(y, m, d, hours.close_minute);

        const data = this.needsCoach
          ? await callWidget("availability", {
              session_type: this.sessionType, duration_mins: this.duration, coach_level: this.coachLevel,
              coach_id: this.coachId, from: from.toISOString(), to: to.toISOString(),
            })
          : await callWidget("capacity", {
              session_type: this.sessionType, duration_mins: this.duration,
              from: from.toISOString(), to: to.toISOString(),
            });
        this.slots = data.slots || [];
        this.renderSlots();
      } catch (e) {
        slotsWrap.innerHTML = `<div class="the108-bw-error">Couldn't load times: ${e.message}</div>`;
      }
    }

    renderSlots() {
      const wrap = this.el.querySelector('[data-role="slots"]');
      if (!this.slots.length) {
        wrap.innerHTML = '<div class="the108-bw-empty">No times available this day. Try another date.</div>';
        return;
      }
      const grid = document.createElement("div");
      grid.className = "the108-bw-slots";
      this.slots.forEach((slot) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "the108-bw-slot";
        const availNote = !this.needsCoach && slot.free_count ? `<span class="price">${fmtPrice(slot.price_cents)} · ${slot.free_count} left</span>` : `<span class="price">${fmtPrice(slot.price_cents)}</span>`;
        btn.innerHTML = `${fmtTime(slot.slot_start)}${availNote}`;
        btn.addEventListener("click", () => {
          this.el.querySelectorAll(".the108-bw-slot").forEach((b) => b.classList.remove("is-selected"));
          btn.classList.add("is-selected");
          this.showReview(slot, 1);
        });
        grid.appendChild(btn);
      });
      wrap.innerHTML = "";
      wrap.appendChild(grid);
    }


    async confirmAndHold(slot, btn, quantity, customer) {
      const originalText = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Holding…";

      try {
        const hold = await callWidget("hold", {
          session_type: this.sessionType,
          duration_mins: this.duration,
          coach_level: this.coachLevel,
          slot_start: slot.slot_start,
          coach_id: slot.coach_id || null,
          quantity,
        });

        // Sign the waiver now, at booking time — not emailed after the fact.
        // Best-effort: a failure here shouldn't strand a held slot, since the
        // hold has already succeeded and the person did agree in the UI.
        if (customer && customer.waiver && !customer.alreadyCovered) {
          try {
            await callWidget("sign_waiver", { name: customer.name, email: customer.email, booking_id: hold.booking_id });
          } catch (e) { /* best-effort */ }
        }

        const properties = {
          Date: new Date(slot.slot_start).toLocaleDateString("en-GB", { timeZone: LONDON_TZ, weekday: "long", day: "numeric", month: "long" }),
          Time: fmtTime(slot.slot_start),
          _booking_id: hold.booking_id,
          _manage_token: hold.manage_token,
        };
        if (customer && customer.name) properties["Booked by"] = customer.name;
        if (this.coachLevel) properties["Coach"] = this.coachName || COACH_LABELS[this.coachLevel];
        if (quantity > 1) {
          const base = this.sessionType.startsWith("cage") ? "Cages" : "Bullpens";
          properties[`Booked (${base})`] = String(quantity);
        }

        const addRes = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [{ id: gidToNumeric(hold.shopify_variant_id), quantity: hold.quantity, properties }],
          }),
        });
        if (!addRes.ok) {
          const errData = await addRes.json().catch(() => ({}));
          throw new Error(errData.description || "Couldn't add to cart.");
        }

        window.location.href = "/cart";
      } catch (e) {
        btn.disabled = false;
        btn.textContent = originalText;
        const review = this.el.querySelector('[data-role="review"]');
        const err = document.createElement("div");
        err.className = "the108-bw-error";
        err.style.marginTop = "8px";
        err.textContent = `Couldn't complete that booking: ${e.message}. Please try again or pick another time.`;
        review.appendChild(err);
        this.backToPicker();
        this.loadAvailability();
      }
    }
  }

  function init() {
    injectStyle();
    document.querySelectorAll(".the108-booking-widget").forEach((el) => {
      if (el.dataset.the108Initialized) return;
      el.dataset.the108Initialized = "true";
      new BookingWidget(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
